<template>
  <div
    ref="messagesContainer"
    class="chatbot-messages"
  >
    <!-- Welcome message when empty -->
    <div
      v-if="!hasMessages && !isLoading"
      class="chatbot-welcome"
    >
      <div class="welcome-icon">
        <i class="pi pi-android" />
      </div>
      <h3>Welcome to Deep Thought</h3>
      <p>The answer to life, the universe, and your Krewebacchus questions.</p>
      <div class="welcome-suggestions">
        <button
          v-for="suggestion in suggestions"
          :key="suggestion"
          class="suggestion-btn"
          @click="sendSuggestion(suggestion)"
        >
          {{ suggestion }}
        </button>
      </div>
    </div>

    <!-- Messages -->
    <template v-else>
      <div
        v-for="message in messages"
        :key="message.id || message.timestamp.getTime()"
        :class="['message', message.role]"
      >
        <div class="message-avatar">
          <i :class="message.role === 'user' ? 'pi pi-user' : 'pi pi-android'" />
        </div>
        <div class="message-content">
          <!-- eslint-disable-next-line vue/no-v-html -- Content is sanitized via formatMessage -->
          <div
            class="message-bubble"
            v-html="formatMessage(message.content)"
          />
          <div class="message-time">
            {{ formatTime(message.timestamp) }}
          </div>
        </div>
      </div>
    </template>

    <!-- Loading indicator -->
    <div
      v-if="isLoading"
      class="message assistant"
    >
      <div class="message-avatar">
        <i class="pi pi-android" />
      </div>
      <div class="message-content">
        <div class="message-bubble loading">
          <div class="loading-content">
            <div class="loading-dots">
              <span class="dot" />
              <span class="dot" />
              <span class="dot" />
            </div>
            <span
              v-if="loadingStatus"
              class="loading-status"
            >{{ loadingStatus }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Error message -->
    <div
      v-if="error"
      class="error-message"
    >
      <i class="pi pi-exclamation-triangle" />
      <span>{{ error }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, watch } from 'vue'

const { messages, isLoading, loadingStatus, error, hasMessages, sendMessage } = useChatbot()

const messagesContainer = ref<HTMLElement | null>(null)

const suggestions = [
  'How do I pay my dues?',
  'How do I join a SubKrewe?',
  'What are Red Shirts?',
]

function sendSuggestion(text: string) {
  sendMessage(text)
}

function formatMessage(content: string): string {
  // Basic markdown-like formatting
  return content
    // Bold
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Links
    .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')
    // Line breaks
    .replace(/\n/g, '<br>')
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

// Auto-scroll to bottom when new messages arrive
watch(
  () => messages.value.length,
  async () => {
    await nextTick()
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  },
)

// Also scroll when loading state changes
watch(isLoading, async () => {
  await nextTick()
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
  }
})
</script>

<style scoped>
.chatbot-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: var(--p-surface-900);
}

/* Welcome state */
.chatbot-welcome {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  padding: 20px;
  color: var(--p-surface-300);
}

.welcome-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--p-primary-500), var(--p-primary-700));
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  box-shadow: 0 0 20px rgba(57, 255, 20, 0.3);
}

.welcome-icon i {
  font-size: 2rem;
  color: var(--p-surface-900);
}

.chatbot-welcome h3 {
  margin: 0 0 8px 0;
  color: var(--p-surface-0);
  font-size: 1.125rem;
}

.chatbot-welcome p {
  margin: 0 0 20px 0;
  font-size: 0.875rem;
}

.welcome-suggestions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.suggestion-btn {
  padding: 10px 16px;
  background: var(--p-surface-800);
  border: 1px solid rgba(57, 255, 20, 0.2);
  border-radius: 12px;
  color: var(--p-surface-200);
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  text-align: left;
}

.suggestion-btn:hover {
  background: var(--p-surface-700);
  border-color: var(--p-primary-500);
  color: var(--p-primary-500);
}

/* Messages */
.message {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.message.user {
  flex-direction: row-reverse;
}

.message-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.message.assistant .message-avatar {
  background: linear-gradient(135deg, var(--p-primary-500), var(--p-primary-700));
  color: var(--p-surface-900);
}

.message.user .message-avatar {
  background: var(--p-surface-600);
  color: var(--p-surface-200);
}

.message-avatar i {
  font-size: 0.875rem;
}

.message-content {
  max-width: 80%;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.message.user .message-content {
  align-items: flex-end;
}

.message-bubble {
  padding: 10px 14px;
  border-radius: 16px;
  font-size: 0.875rem;
  line-height: 1.5;
  word-wrap: break-word;
}

.message.assistant .message-bubble {
  background: var(--p-surface-700);
  color: var(--p-surface-100);
  border-bottom-left-radius: 4px;
}

.message.user .message-bubble {
  background: linear-gradient(135deg, var(--p-primary-600), var(--p-primary-700));
  color: var(--p-surface-900);
  border-bottom-right-radius: 4px;
}

.message-bubble :deep(a) {
  color: var(--p-primary-400);
  text-decoration: underline;
}

.message.user .message-bubble :deep(a) {
  color: var(--p-surface-900);
}

.message-time {
  font-size: 0.7rem;
  color: var(--p-surface-500);
  padding: 0 4px;
}

/* Loading animation */
.message-bubble.loading {
  padding: 12px 16px;
}

.loading-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}

.loading-dots {
  display: flex;
  gap: 4px;
}

.loading-status {
  font-size: 0.75rem;
  color: var(--p-primary-400);
  font-style: italic;
  animation: fade-pulse 2s ease-in-out infinite;
}

@keyframes fade-pulse {
  0%, 100% {
    opacity: 0.7;
  }
  50% {
    opacity: 1;
  }
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--p-primary-500);
  animation: bounce 1.4s ease-in-out infinite;
}

.dot:nth-child(1) {
  animation-delay: 0s;
}

.dot:nth-child(2) {
  animation-delay: 0.2s;
}

.dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes bounce {
  0%, 80%, 100% {
    transform: translateY(0);
    opacity: 0.5;
  }
  40% {
    transform: translateY(-6px);
    opacity: 1;
  }
}

/* Error message */
.error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: rgba(255, 62, 94, 0.1);
  border: 1px solid rgba(255, 62, 94, 0.3);
  border-radius: 12px;
  color: var(--p-danger-500);
  font-size: 0.875rem;
}

.error-message i {
  font-size: 1rem;
}

/* Scrollbar styling */
.chatbot-messages::-webkit-scrollbar {
  width: 6px;
}

.chatbot-messages::-webkit-scrollbar-track {
  background: transparent;
}

.chatbot-messages::-webkit-scrollbar-thumb {
  background: var(--p-surface-600);
  border-radius: 3px;
}

.chatbot-messages::-webkit-scrollbar-thumb:hover {
  background: var(--p-surface-500);
}
</style>
